import { useState } from 'react';
import { createPortal } from 'react-dom';
import { RiShieldKeyholeLine, RiLoader4Line, RiCloseLine, RiInformationLine } from '@remixicon/react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';
import { ROLES, normalizeRole } from '@/lib/permissions';

// PROD-only recovery action. Kept out of DEV / LOCAL so accidental clicks
// during development can't wipe a real TOTP secret.
const IS_PROD = process.env.REACT_APP_ENVIRONMENT === 'PROD';

/**
 * Reset MFA button — clears a user's TOTP factors via
 * `PATCH /users/{userId}/security/mfa { reset: true }`. The user is
 * prompted to enroll again on their next sign-in.
 *
 * Visibility gates (all must hold, else the component renders null):
 *   - Environment is PROD
 *   - Viewer is admin or super_admin
 *   - Viewer is NOT the same user as the target (self-reset would wipe
 *     the viewer's own TOTP mid-session and lock them out until they
 *     re-enroll — Account Settings has the intended self-service path)
 *   - Target user has MFA enrolled (`mfaEnabled === true`) — no point
 *     offering a reset on a user with nothing to reset.
 *   - `userId` is present
 *
 * A confirmation modal (portal, z-[10000] so it stacks above drawers)
 * runs before the PATCH fires — this is a destructive action for the
 * target user's second factor.
 */
const ResetMfaButton = ({ userId, memberName, mfaEnabled, className = '', variant = 'default' }) => {
  const { userProfile } = useAuthStore();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Sticky "just reset" flag — hides the button for the rest of the
  // current drawer render. Reopening the drawer rehydrates from the
  // React Query cache, so we also patch that cache below to flip
  // mfa_enabled to false — otherwise the button would come back.
  const [didReset, setDidReset] = useState(false);
  // Step-up MFA state — admin verifies their own TOTP code before the
  // destructive PATCH fires. When the admin has no factor (e.g. internal
  // @gotrustjet accounts bypass MFA), verification is skipped and the
  // modal falls back to a plain confirm.
  const [adminFactorId, setAdminFactorId] = useState(null);
  const [factorsLoading, setFactorsLoading] = useState(false);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState(null);
  // Tracks that the admin has already passed the TOTP challenge in the
  // current modal session, so a failed PATCH lets them retry the RESET
  // without going through mfa.verify again against a now-rotated code
  // (Supabase TOTP rotates every ~30s — reusing an old code would show
  // "Invalid code" and hide the actual PATCH failure).
  const [stepUpDone, setStepUpDone] = useState(false);

  if (!IS_PROD) return null;
  if (!userId) return null;
  if (mfaEnabled === false) return null; // undefined still shows (BE hasn't returned the field yet)
  if (didReset) return null;
  // Self-reset guard — refuse to wipe the viewer's own TOTP factor
  // mid-session. AddMemberDrawer already hides Edit for self-rows,
  // but OrganizationDrawer's owner card doesn't, so an admin who
  // owns an org they open would otherwise see this button on
  // themselves. Enforcing here covers both surfaces.
  if (userProfile?.user_id && userProfile.user_id === userId) return null;
  const viewerRole = normalizeRole(userProfile?.role);
  const canReset = viewerRole === ROLES.ADMIN || viewerRole === ROLES.SUPER_ADMIN;
  if (!canReset) return null;

  // Lazily probe the admin's TOTP factors the first time the modal opens.
  // Kept outside a useEffect so we don't fire an extra network call on
  // renders where the modal is closed.
  const openModal = async () => {
    setConfirmOpen(true);
    setCode('');
    setCodeError(null);
    setStepUpDone(false);
    if (adminFactorId !== null || factorsLoading) return;
    setFactorsLoading(true);
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      const verified = (data?.totp || []).find((f) => f.status === 'verified');
      // Empty string as sentinel for "loaded, no factor" so the ternary
      // below can distinguish loaded-no-factor from not-yet-loaded.
      setAdminFactorId(verified?.id || '');
    } catch (err) {
      console.warn('ResetMfaButton: listFactors failed', err);
      setAdminFactorId(''); // treat as "no factor" — allow the fallback path
    } finally {
      setFactorsLoading(false);
    }
  };

  const requiresStepUp = !!adminFactorId; // truthy string = a real factor id
  // Once the admin has already passed the TOTP challenge in this modal
  // session, a code isn't required to enable Verify & Reset again.
  const codeValid = /^\d{6}$/.test(code);
  const canConfirm =
    !saving && !factorsLoading && (!requiresStepUp || stepUpDone || codeValid);

  const patchCaches = () => {
    // Patch every cached surface that carries this user's row so the
    // button stays hidden after the drawer is reopened. See handleReset
    // callers in AddMemberDrawer + OrganizationDrawer for the shapes.
    queryClient.setQueriesData(
      { predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'orgMembers' },
      (old) => Array.isArray(old)
        ? old.map((m) => (m?.user_id === userId ? { ...m, mfa_enabled: false } : m))
        : old,
    );
    // OrganizationDrawer's owner cache is keyed ['user', userprofile_id]
    // ([:634]) while ResetMfaButton receives auth.users.id — so a
    // key-position compare would never match. Match on the row's own
    // user_id instead.
    queryClient.setQueriesData(
      { predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'user' },
      (old) => (old && old.user_id === userId ? { ...old, mfa_enabled: false } : old),
    );
  };

  const handleReset = async () => {
    if (!canConfirm) return;
    setSaving(true);
    setCodeError(null);
    try {
      // Step-up: verify the admin's own TOTP before firing the PATCH.
      // Skipped when (a) admin has no verified factor (internal
      // @gotrustjet accounts) or (b) they already passed step-up
      // earlier in this modal session and only the PATCH failed —
      // reissuing challenge/verify against a rotated code would show
      // a misleading "Invalid code" instead of the real PATCH error.
      if (requiresStepUp && !stepUpDone) {
        const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
          factorId: adminFactorId,
        });
        if (challengeError || !challengeData?.id) {
          throw new Error(challengeError?.message || 'Failed to create MFA challenge');
        }
        const { error: verifyError } = await supabase.auth.mfa.verify({
          factorId: adminFactorId,
          challengeId: challengeData.id,
          code,
        });
        if (verifyError) {
          // Wrong / expired code — leave the modal open so the admin can
          // retry with a fresh 6-digit code.
          setCodeError(verifyError.message || 'Invalid code. Please try again.');
          setCode('');
          setSaving(false);
          return;
        }
        setStepUpDone(true);
      }

      await apiClient.resetUserMfa(userId);
      toast.success('MFA reset — the user will be prompted to enroll on next sign-in.', {
        position: 'bottom-right',
      });
      setConfirmOpen(false);
      setDidReset(true);
      patchCaches();
    } catch (err) {
      toast.error(err?.message || 'Failed to reset MFA', { position: 'bottom-right' });
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    if (saving) return;
    setConfirmOpen(false);
    setCode('');
    setCodeError(null);
  };

  // Two visual variants:
  //   default → pill button used in AddMemberDrawer's Status section
  //   compact → text-link style used in OrganizationDrawer's Owner block
  const btnClass = variant === 'compact'
    ? 'inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:text-amber-800 hover:underline'
    : 'w-full flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium rounded-full border border-amber-300 text-amber-700 hover:bg-amber-50 transition-colors';

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className={`${btnClass} ${className}`}
      >
        <RiShieldKeyholeLine className="w-4 h-4" />
        Reset MFA
      </button>
      {confirmOpen && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeModal}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-amber-700">Reset MFA?</h2>
              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
                aria-label="Close"
              >
                <RiCloseLine className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-700 leading-relaxed">
                This will clear {memberName ? <span className="font-semibold">{memberName}</span> : 'this user'}'s
                two-factor authentication. They'll be prompted to enroll a new authenticator on their next sign-in.
              </p>
              <p className="text-xs text-gray-500">
                Only reset when the user has confirmed they lost access to their authenticator app.
              </p>

              {/* Step-up MFA — while the admin's factors are being probed
                  show a spinner; once loaded, show either the code input
                  (admin has a factor) or a subtle info line (admin has no
                  factor — internal @gotrustjet account). */}
              {factorsLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                  <RiLoader4Line className="w-4 h-4 animate-spin" />
                  Preparing verification…
                </div>
              ) : requiresStepUp ? (
                <div className="pt-2 border-t border-gray-100">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Confirm with your MFA code
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                      if (codeError) setCodeError(null);
                    }}
                    placeholder="123456"
                    className={`w-full px-4 py-2.5 border rounded-lg text-sm tracking-widest text-center font-mono focus:outline-none focus:ring-2 ${
                      codeError
                        ? 'border-red-300 focus:ring-red-200'
                        : 'border-gray-200 focus:ring-primary-200 focus:border-primary-300'
                    }`}
                    disabled={saving}
                    autoFocus
                  />
                  {codeError && (
                    <p className="text-xs text-red-600 mt-1.5">{codeError}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1.5">
                    Open your authenticator app and enter the 6-digit code for your own account.
                  </p>
                </div>
              ) : (
                <div className="pt-2 border-t border-gray-100 flex items-start gap-2 text-xs text-gray-500">
                  <RiInformationLine className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
                  <span>Your account has no MFA factor to verify against — proceeding without step-up.</span>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={saving}
                  className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={!canConfirm}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white rounded-full bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving && <RiLoader4Line className="w-4 h-4 animate-spin" />}
                  {saving ? 'Resetting…' : requiresStepUp ? 'Verify & Reset' : 'Reset MFA'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default ResetMfaButton;
